<template>
  <div>
    <section class="hero-slider">
      <div class="slides">
        <transition name="fade" mode="out-in">
          <div class="slide" :key="current" v-if="currentSlide" >
            <template v-if="currentSlide.type === 'image'">
              <img :src="currentSlide.src" alt="hero image" class="hero-media" />
            </template>
            <template v-else>
              <video
                ref="setVideoRef"
                class="hero-media"
                :src="currentSlide.src"
                playsinline
                loop
                muted
                controls="false"
                @play="isVideoPlaying = true"
                @pause="isVideoPlaying = false"
                @loadedmetadata="isVideoMuted = $event.target.muted"
                @volumechange="isVideoMuted = $event.target.muted"
              />
            </template>

            <div class="hero-overlay container">
              <h1 class="hero-title">{{ currentSlide.title || COMPANYFULLNAME }}</h1>
              <p class="hero-desc">{{ currentSlide.subtitle }}</p>
              <div class="hero-actions">
                <a v-if="currentSlide.showReadMore" class="btn btn-brand" :href="currentSlide.readMoreLink || '#'" target="_self" rel="noopener">READ MORE</a>
                <button v-if="currentSlide.type === 'video'" class="btn btn-outline" @click="toggleVideo">
                  {{ isVideoPlaying ? 'PAUSE' : 'PLAY' }}
                </button>
                <button v-if="currentSlide.type === 'video'" class="btn btn-outline ms-2" @click="toggleMute">
                  {{ isVideoMuted ? 'UNMUTE' : 'MUTE' }}
                </button>
              </div>

              <!-- bottom title: rendered only when current slide provides bottomTitle -->
              <div v-if="currentSlide.bottomTitle" class="hero-title" style="padding-top: 50px;">
                {{ currentSlide.bottomTitle }}
              </div>
            </div>
          </div>
        </transition>
      </div>

      <button class="nav prev" @click="prev">&#10094;</button>
      <button class="nav next" @click="next">&#10095;</button>

      <div class="dots">
        <button
          v-for="(s, i) in slides"
          :key="i"
          :class="{ active: i === current }"
          @click="goTo(i)"
          :title="s.type"
        />
      </div>
    </section> <!-- end hero-slider -->    

    <!-- The rest of the page content below -->
    <section class="py-4">
      <div class="container">
        <div class="row g-4">
          <div class="col-lg-7">
            <div class="p-4 h-100">
              <h5 class="fw-bold mb-2"><span style="color:var(--brand)">ABOUT US</span></h5>
              <p class="mb-0" >
                {{ COMPANYFULLNAME }} was established August 1, 2007. The firm specializes in land development projects. These include: commercial, industrial, multi-family, single family and redevelopment. 
                Richview provides our clients with unique and cost effective solutions with respect to land development and services.
              </p>
            </div>
          </div>

          <div class="col-lg-5">
            <div class="p-4 h-100">
              <h5 class="fw-bold mb-3">LATEST NEWS</h5>

              <!-- modern news list (replaces previous simple UL) -->
              <div class="news-list">
                <ul class="news-items list-unstyled mb-0">
                  <li
                    v-for="item in latestNews"
                    :key="item.newsId"
                    class="news-item d-flex flex-column py-3"
                  >
                    <!-- content: title + subtitle -->
                    <div class="news-body">
                      <a href="#" class="news-title d-block text-decoration-none" @click.prevent="goToNewsDetail(item.newsId)">
                        {{ item.mainTitle }}
                      </a>
                      <div class="news-subtitle mt-1">
                        {{ item.subTitle }}
                      </div>
                    </div>

                    <!-- footer row: date placed on the bottom-left -->
                    <div class="news-footer mt-3">
                      <div class="news-date">
                        {{ formatDate(item.strPublishDate) }}
                      </div>
                    </div>
                  </li>
                </ul>
                <div v-if="latestNews.length === 0" class="news-empty small">No news available.</div>
              </div>
            </div>
          </div>
        </div>

        <hr class="hr-soft">
        
        <div class="p-4">
          <h5 class="fw-bold mb-2">BEST PRACTICE</h5>
          <p class="mb-0">
            Our firm provides cost-effective solutions for land development and redevelopment.
            We serve clients in commercial, multifamily, single-family and urban redevelopment projects in Alberta, British Columbia, Saskatchewan, Ontario and Manitoba.
          </p>
        </div>

        <!-- case videos: full-width block to show success-case video and list -->
        <section class="case-videos">
          <div class="container" style="padding-bottom: 30px;">        
            <div class="case-grid">
              <!-- main video area -->
              <div class="case-main">
                <!-- main media: video when isVideo === true, otherwise image -->
                <div class="case-media">
                  <!-- media: video OR image share the same visual class -->
                  <video
                    v-if="currentCase && currentCase.isVideo"
                    ref="caseVideoRef"
                    class="case-media-element case-video"
                    :src="`/projects/${currentCase.projectId}.mp4`"
                    playsinline
                    muted
                    controls
                    :poster="`/projects/thumbs/${currentCase.projectId}.png`"
                  ></video>

                  <img
                    v-else-if="currentCase"
                    class="case-media-element case-image"
                    :src="`/projects/${currentCase.projectId}.png` || ''"
                    :alt="currentCase.title || ''"
                    @error="onCaseImageError"
                  />
                </div>

                <!-- caption / description (render only when currentCase exists) -->
                <div class="case-caption" v-if="currentCase">
                  <h4 class="case-title">{{ currentCase.catalogName }} <span class="sep"> — </span> {{ currentCase.title }}</h4>
                  <p class="case-desc">{{ currentCase.description }}</p>
                </div>
              </div>

              <!-- side list of case thumbnails -->
              <aside class="case-list" aria-label="Case list">
                <ul>
                  <li
                    v-for="(c, i) in bestProjects"
                    :key="i"
                    :class="{ active: i === currentCaseIndex }"
                    @click="selectCase(i)"
                  >
                    <img :src="getThumbSrc(c)" :alt="c.catalogName"  @error="onCaseImageError"/>
                    <div class="case-item-meta">
                      {{ c.catalogName }}
                    </div>
                  </li>
                </ul>
              </aside>
            </div>
          </div>
        </section>

      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, getCurrentInstance, nextTick } from 'vue';
import { COMPANYFULLNAME, MONTHS, TIPSTAG_ERROR } from '@/constants';
import apiClient from '@/apiClient';
import { useRouter } from 'vue-router';
import defaultCaseThumb from '@/assets/defaultError.png';

const { proxy } = getCurrentInstance();

const router = useRouter();
async function goToNewsDetail(newsId) {
  
  router.push({ name: 'NewsDetail', params: { newsId: newsId } });
}

// Configure slides: type = 'image' | 'video', replace src with actual project paths
const slides = [
  { 
    type: 'image',
    src: new URL('@/assets/homepagebg.jpg', import.meta.url).href,
    // subtitle includes explicit newlines; keep using '\n' so CSS can render them as line breaks
    subtitle: 'We live with the LAND, not on it, DEVELOPMENT should not be disturbance',
    title: 'Richview Engineering Inc.', // Slide-specific main title (customizable)
    showReadMore: true, // Controls whether the READ MORE button is shown
    readMoreLink: '/about', // Optional READ MORE link
    bottomTitle: '' // Omit or set empty to hide
  },
  { 
    type: 'image',
    src: new URL('@/assets/homepagebg3.png', import.meta.url).href,
    subtitle: '',
    title: 'Specializing in Land Development\nPlanning • Engineering • Surveying',
    showReadMore: false,
    bottomTitle: 'WWW.RICHVIEWENG.COM' 
  },
];

const current = ref(0);
const intervalMs = 6000;
let timer = null;
const isVideoPlaying = ref(false);
// Videos start muted; the user can turn the sound on/off
const isVideoMuted = ref(true);

// Store video refs (only present when the slide is a video)
const videoRefs = ref([]);
function setVideoRef(el) {
  // Vue passes null then element on repeated renders — keep the latest reference
  if (!el) return;
  videoRefs.value[current.value] = el;
}

const currentSlide = computed(() => slides[current.value]);


function getThumbSrc(project) {
  if(!project) return defaultCaseThumb;

  if(project.projectId) {
    if(project.isVideo) {
      return `/projects/thumbs/${project.projectId}.png`;
    } else {
      return `/projects/${project.projectId}.png`;
    }
  }  

  return defaultCaseThumb;
}


function playCurrentVideo() {
  
    const v = videoRefs.value[current.value];
    if (v && v.paused) {
      v.play().catch(() => {});
      isVideoPlaying.value = !v.paused;
    }
    else {
      isVideoPlaying.value = false;
    }
}

function pauseAllVideos() {
  videoRefs.value.forEach(v => {
    try { v && !v.paused && v.pause(); } catch (e) { console.error(e); }
  });
  isVideoPlaying.value = false;
}

function next() {
  pauseAllVideos();
  current.value = (current.value + 1) % slides.length;
}
function prev() {
  pauseAllVideos();
  current.value = (current.value - 1 + slides.length) % slides.length;
}
function goTo(i) {
  pauseAllVideos();
  current.value = i;
}
function startTimer() {
  stopTimer();
  timer = setInterval(() => { next(); }, intervalMs);
}

function stopTimer() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
}

function toggleVideo() {
  const v = videoRefs.value[current.value];
  if (!v) return;
  if (v.paused) {
    v.play().catch(() => {});
    isVideoPlaying.value = !v.paused;
  } else {
    v.pause();
    isVideoPlaying.value = false;
  }
}

function toggleMute() {
  const v = videoRefs.value[current.value];
  if (!v) return;
  v.muted = !v.muted;
  isVideoMuted.value = v.muted;
}

function onVideoEnded() {
  // When the video ends, mark as not playing and switch to the next slide
  isVideoPlaying.value = false;
  next();
}

// Watch isVideoPlaying: stop auto-rotation and attach 'ended' handler when playing; remove handler and restart auto-rotation when paused/ended
watch(isVideoPlaying, (playing) => {
  const v = videoRefs.value[current.value];
  if (playing) {
    stopTimer();
    if (v && !v._endedHandlerAttached) {
      v.addEventListener('ended', onVideoEnded);
      v._endedHandlerAttached = true;
    }
  } else {
    if (v && v._endedHandlerAttached) {
      v.removeEventListener('ended', onVideoEnded);
      v._endedHandlerAttached = false;
    }
    // Restart only if there's no existing timer
    if (!timer) startTimer();
  }
});

watch(current, () => {
  // Small delay to wait for the video ref to update
  setTimeout(() => playCurrentVideo(), 50);
});

// Auto-rotate slides (will advance even when switching to a video, but video autoplay may be restricted in some browsers)
onMounted(() => {
  timer = setInterval(() => { next(); }, intervalMs);
  // Initial attempt to play (if the current slide is a video)
  setTimeout(playCurrentVideo, 500);
});

onBeforeUnmount(() => {
  clearInterval(timer);
  pauseAllVideos();
});


const caseVideoRef = ref(null);
const currentCaseIndex = ref(0);
const bestProjects = ref([]);
const currentCase = computed(() => {
  if (!Array.isArray(bestProjects.value) || bestProjects.value.length === 0) return null;
  const idx = Math.min(Math.max(0, currentCaseIndex.value), bestProjects.value.length - 1);
  return bestProjects.value[idx];
});

// pause all videos inside the case-media area (safely)
function pauseAllCaseVideos() {
  try {
    const vids = document.querySelectorAll('.case-media video');
    vids.forEach(v => {
      try {
        v.pause();
        v.currentTime = 0;
      } catch (e) { /* ignore */ }
    });
  } catch (e) { /* ignore */ }
}

function selectCase(i) {
  // pause any currently playing case videos before switching
  pauseAllCaseVideos();

  // switch index
  currentCaseIndex.value = i;

  // wait DOM update so new <video> element is mounted, then try play
  nextTick(() => {
    const v = caseVideoRef.value;
    if (v) {
      try {
        v.muted = true;
        v.currentTime = 0;
        v.play().catch(() => {});
      } catch (e) { /* ignore */ }
    }
  });
}


async function loadLatestNewsList() {
 try {
    const response = await apiClient.post('/Main/getLatestNewsList', {});

    if(response && response.data && Array.isArray(response.data.news)) {
      // Update news list with fetched data
      latestNews.value = response.data.news; 
    }    

 } catch (e) {   
   if(e.response && e.response.data && e.response.status === 400) {    
     proxy.$handleTips(e.response.data.tag, e.response.data.msg, e);
   } else {
    proxy.$handleTips(TIPSTAG_ERROR, 'Network error occurred. Please try again later.', e);
  } 
}
}


async function loadBestProjects() {
 try {
    const response = await apiClient.post('/Main/getBestExpProjects');
    if (response && response.data && Array.isArray(response.data.bestProjects)) {
      bestProjects.value = response.data.bestProjects;
      // ensure index is within range and points to first item if none selected
      if (bestProjects.value.length && (currentCaseIndex.value < 0 || currentCaseIndex.value >= bestProjects.value.length)) {
        currentCaseIndex.value = 0;
      }
    }        

 } catch (e) {   
   if(e.response && e.response.data && e.response.status === 400) {    
     proxy.$handleTips(e.response.data.tag, e.response.data.msg, e);
   } else {
    proxy.$handleTips(TIPSTAG_ERROR, 'Network error occurred. Please try again later.', e);
  } 
 }
}


// keep this reactive; replace with fetch call to backend when ready
const latestNews = ref([]);

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d);  
  return MONTHS[dt.getMonth()] + '. ' + dt.getDate() + ', ' + dt.getFullYear();  
}


onMounted(() => {
  // Autoplay the first case video
  const v = caseVideoRef.value;
  if (v) {
    v.muted = true;
    v.play().catch(() => {});
  }

  loadLatestNewsList();
  loadBestProjects();
});


function onCaseImageError(e) {
  if (!e?.target) return;
  e.target.onerror = null;
  e.target.src = defaultCaseThumb;
}

// ensure you already have:
// const caseVideoRef = ref(null);
// const currentCase = computed(...)
// const isVideoPlaying = ref(false);

watch(currentCase, async (c) => {
  // wait DOM update so video element is mounted or removed
  await nextTick();

  const v = caseVideoRef.value;
  if (c && c.isVideo && v) {
    try {
      // each case video starts muted, the user can unmute it with the player controls
      v.muted = true;
      v.currentTime = 0;
      await v.play().catch(() => {}); // ignore play rejection
      isVideoPlaying.value = !v.paused;
    } catch (e) {
      // ignore
    }
  } else if (v) {
    // pause/reset when switching away or no video
    try {
      v.pause();
      v.currentTime = 0;
      isVideoPlaying.value = false;
    } catch (e) { /* ignore */ }
  }
});
</script>

<style scoped>
.hero-slider {
  position: relative;
  height: 520px;
  overflow: hidden;
  background: #000;
}
.hero-media {
  width: 100%;
  height: 520px;
  object-fit: cover;
  display: block;
}
.hero-overlay {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display:flex;
  flex-direction: column;
  justify-content: center;
  color: #fff;
  pointer-events: none;
  text-shadow: 0 4px 12px rgba(0,0,0,.6);
}
.hero-title {
  color: #ffffff; /* Use pure white for main title text */
  font-weight: 700;
  font-size: 2.6rem;
  line-height: 1.05;
  display: inline-block;

  /* Preserve newline characters so multi-line title renders */
  white-space: pre-line;

  /* Subtle translucent backing to improve legibility on busy backgrounds */
  background: rgba(0, 0, 0, 0.12);
  padding: 10px 14px;
  border-radius: 6px;

  /* Text shadow for contrast */
  text-shadow: 0 6px 20px rgba(0, 0, 0, 0.62);

  /* Prevent overflow on small screens */
  max-width: 94%;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 767px) {
  .hero-title {
    font-size: 1.6rem;
    padding: 8px 10px;
  }
}

.hero-desc {
  margin-bottom: 1rem;
  pointer-events: auto;
  color: rgba(255,255,255,0.95); /* Soft but prominent white for better visibility */
  font-size: 1.6rem;
  line-height: 1.35;
  text-shadow: 0 4px 12px rgba(0,0,0,0.6); /* Improve contrast against busy backgrounds */
  display: inline-block;
  background: rgba(0,0,0,0.18); /* Subtle translucent backing to increase legibility */
  padding: 6px 12px;
  border-radius: 6px;
  white-space: pre-line; /* Preserve newline characters in subtitle text (\n -> line break) */
}
.hero-actions { pointer-events: auto; }

.nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0,0,0,.35);
  color: #fff;
  border: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  cursor: pointer;
}
.nav.prev { left: 12px; }
.nav.next { right: 12px; }

.dots {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 14px;
  display:flex;
  gap:8px;
}
.dots button {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,.45);
  cursor: pointer;
}
.dots button.active { background: var(--brand, #0d6efd); }

.fade-enter-active, .fade-leave-active { transition: opacity .6s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.hero-bottom-title {
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 18px;
  background: rgba(0,0,0,0.45);
  color: #ffffff;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 1rem;
  pointer-events: auto;
  box-shadow: 0 6px 18px rgba(0,0,0,0.45);
  max-width: calc(100% - 36px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* case videos section */
.case-videos {
  width: 100%;
  /* height similar to hero area */
  min-height: 480px;
  padding: 28px 0;
  background: linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.04));
}
.case-heading {
  color: var(--brand, #0d6efd);
  font-weight: 700;
  margin-bottom: 14px;
  pointer-events: auto;
}

.case-grid {
  display: flex;
  gap: 18px;
  align-items: stretch;
}

/* main video area stretches to match page width */
.case-main {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* unified media element styling for video and image */
.case-media-element {
  width: 100%;  
  height: 100%;            /* same visual height for both media types */
  object-fit: contain;
  display: block;
  border-radius: 6px;
  background: #000;
  box-shadow: 0 8px 26px rgba(0,0,0,0.25);
  /* ensure content scales and is clipped to rounded corners */
  overflow: hidden;
  object-position: center;
}

/* responsive: reduce height on small screens */
@media (max-width: 991px) {
  .case-media-element { height: 320px; }
}
@media (max-width: 480px) {
  .case-media-element { height: 220px; border-radius: 4px; }
}

/* caption area below video */
.case-caption { 
  padding: 10px 14px;
  border-radius: 6px;
  pointer-events: auto;
}
.case-caption .case-title { font-size: 1.0rem; font-weight: 400; margin:0 0 6px; color: #e0e4ea; }
.case-caption .case-desc { 
  margin: 0; 
  color: #e0e4ea; 
  font-size: 0.9rem;
}

/* side list of thumbnails */
.case-list {
  width: 220px;
  flex: 0 0 220px;
  pointer-events: auto;
}
.case-list ul { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:10px; }
.case-list li {
  display:flex;
  gap:10px;
  align-items:center;
  cursor:pointer;
  padding:8px;
  border-radius:6px;
  transition: background .15s, transform .12s;  
}
.case-list li.active { background: rgba(13,110,253,0.08); transform: translateX(2px); }
.case-list img { width: 90px; height: 56px; object-fit:cover; border-radius:4px; }

.case-item-meta { font-size: 0.9rem; color: #d6cece; }

/* small screens: stack list under video */
@media (max-width: 991px) {
  .case-grid { flex-direction: column; }
  .case-list { width: 100%; flex: 0 0 auto; order: 2; }
  .case-video { height: 320px; }
}

/* --- News list styles: modern, roomy, matches existing site tone --- */

.news-list { pointer-events: auto; }

.news-items {
  background: transparent; /* match homepage (no boxed background) */
  border-radius: 0;
  padding: 0;
  box-shadow: none;
}

/* single item */
.news-item {
  border-bottom: 1px solid rgba(255,255,255,0.03);
  padding-left: 6px;
  padding-right: 6px;
}
.news-item:last-child { border-bottom: none; }

/* title */
.news-title {
  color: rgba(255,255,255,0.96);
  font-weight: 600;
  font-size: 1rem;
  transition: color .12s ease, transform .08s ease;
}
.news-title:hover {
  color: #ffffff;
  transform: translateX(3px);
  text-decoration: none;
}

/* subtitle / summary */
.news-subtitle {
  color: rgba(255,255,255,0.72);
  font-size: 0.9rem;
}

/* footer row (bottom of item) */
.news-footer {
  display: flex;
  justify-content: flex-start; /* date on the left */
  align-items: center;
}

/* date style placed at bottom-left */
.news-date {
  color: rgba(255,255,255,0.55);
  font-size: 0.85rem;
  line-height: 1;
}

/* responsive adjustments */
@media (min-width: 768px) {
  .news-item { padding: 10px 6px; }
  .news-subtitle { margin-top: 6px; }
}

@media (max-width: 767.98px) {
  .news-item { padding: 8px 6px; }
  .news-footer { margin-top: 8px; }
}

.news-empty {
  color: rgba(255,255,255,0.78); 
}

/* subtle item hover (keeps page visual consistent) */
.news-item:hover {
  background: transparent;
}

.sep {
  color: rgba(255,255,255,0.48);
  margin: 0 6px;
}

</style>
